import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import {
  CONFIGURATION_SETTING_KEYS,
  EEntityType,
  ERelationType,
  ETicketType,
  ICorporateDesign,
} from '@web/dtos';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { ERoute } from '../../../constants/routes.enum';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';

@Component({
  selector: 'aw-ticket-type-toggle',
  imports: [NgClass, AppendTestIdPipe, NgStyle, FilterTextsPipe, LanguagePipe],
  templateUrl: './ticket-type-toggle.component.html',
})
export class TicketTypeToggleComponent implements OnDestroy {
  private readonly router: Router = inject(Router);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);

  private readonly shownOnUrls: string[] = [
    '/',
    `/${ERoute.directTickets}`,
    `/${ERoute.subscriptions}`,
  ];
  private readonly subscription: Subscription = new Subscription();

  protected readonly TicketType: typeof ETicketType = ETicketType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  public readonly isToggleVisible: Signal<boolean> = computed(
    () => this.isShownOnUrl() && this.hasBothOptions()
  );
  public readonly isShownOnUrl: WritableSignal<boolean> = signal(false);
  public readonly hasBothOptions: Signal<boolean> = computed(
    () =>
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsDirectSaleActive
      ) &&
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsSubscriptionActive
      )
  );
  public readonly currentTicketType: WritableSignal<ETicketType | undefined> =
    signal(undefined);

  constructor() {
    this.setToggleState(this.router.routerState.snapshot.url);
    this.subscription.add(
      this.router.events.subscribe((event: any) => {
        this.setToggleState(event.url);
      })
    );
  }

  public selectSubscription(): void {
    this.router.navigate([`/${ERoute.subscriptions}`]);
  }
  public selectDirect(): void {
    this.router.navigate([`/${ERoute.directTickets}`]);
  }

  private setToggleState(url: string): void {
    this.isShownOnUrl.set(this.shownOnUrls.includes(url));
    if (url === `/${ERoute.directTickets}`) {
      this.currentTicketType.set(ETicketType.Direct);
    } else if (url === `/${ERoute.subscriptions}`) {
      this.currentTicketType.set(ETicketType.Subscription);
    } else {
      this.currentTicketType.set(undefined);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  determineBackgroundColor(ticketType: ETicketType): string {
    const isCorrect: boolean = this.currentTicketType() === ticketType;

    if (isCorrect) {
      return this.corporateDesignService.isDarkModeActive()
        ? this.corporateDesign().primaryDarkColor
        : this.corporateDesign().primaryColor;
    }

    return 'transparent';
  }
}
